import { Link } from "react-router-dom";
import { Waves, BookOpen, Calculator, Landmark, Languages, Palette, ArrowRight, Sparkles } from "lucide-react";

export default function Landing() {
  const subjects = [
    { icon: Calculator, name: "Math", color: "bg-teal-50 text-teal-800 border-teal-100" },
    { icon: BookOpen, name: "Reading & Writing", color: "bg-sky-50 text-sky-800 border-sky-100" },
    { icon: Waves, name: "Oceanography", color: "bg-cyan-50 text-cyan-800 border-cyan-100" },
    { icon: Landmark, name: "History", color: "bg-amber-50 text-amber-800 border-amber-100" },
    { icon: Languages, name: "Spanish", color: "bg-rose-50 text-rose-800 border-rose-100" },
    { icon: Palette, name: "Creative Tech & Art", color: "bg-violet-50 text-violet-800 border-violet-100" },
  ];
  return (
    <div className="min-h-screen stripe">
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-2xl bg-teal-700 text-white"><Waves size={18} strokeWidth={2.5} /></span>
          <span className="font-display text-xl font-bold text-slate-900">Oceanic Learn</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-login" className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-white">Log in</Link>
          <Link to="/register" data-testid="landing-register" className="px-4 py-2 rounded-full text-sm font-semibold bg-teal-700 text-white hover:bg-teal-800 transition-colors">Get started</Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-8 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="chip mb-6"><Sparkles size={12} /> Built for focus. Designed to feel calm.</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">A homeschool that fits <span className="text-teal-700">how you actually learn.</span></h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">Two short missions a day. Clear next steps. Real progress. An AI tutor when you're stuck, and a Beach Lab that turns your local coast into a classroom.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="landing-cta-start" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-700 text-white font-semibold hover:bg-teal-800 transition-colors">Start learning <ArrowRight size={16} /></Link>
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 bg-white text-slate-800 font-semibold hover:border-teal-300">I already have an account</Link>
          </div>
        </div>
        <div className="relative">
          <div className="grid grid-cols-2 gap-4">
            {subjects.map((s) => (
              <div key={s.name} className={`p-5 rounded-3xl border ${s.color} flex flex-col gap-3`}>
                <s.icon size={22} strokeWidth={2.4} />
                <div className="font-display font-semibold">{s.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-6">
        {[
          { title: "Two missions a day", body: "Never more than two. Clear start, clear finish." },
          { title: "AI tutor, not answer key", body: "Get tiny steps and one guiding question — not the answer." },
          { title: "Beach Lab", body: "Your local beach becomes real coursework in oceanography." },
        ].map((c) => (
          <div key={c.title} className="p-6 rounded-3xl bg-white border border-slate-200 shadow-sm">
            <div className="font-display text-lg font-semibold text-slate-900">{c.title}</div>
            <p className="mt-2 text-slate-600 text-sm leading-relaxed">{c.body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
